import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { uploadDeliveryEvidence, setServiceEvidenceUrl } from '../../../services/api/trips-ui';
import Swal from 'sweetalert2';
import imageCompression from 'browser-image-compression';

function DeliveryEvidence({ show, onHide, serviceId, type, onUploadComplete }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setPreview(null);
      setFile(null);
    }
  }, [show]);

  const handleClose = () => {
    onHide();
  };

  const handleCaptureClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setLoading(true);
    try {
      const options = {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(selectedFile, options);
      setFile(compressedFile);
      setPreview(URL.createObjectURL(compressedFile));
    } catch (error) {
      console.error('Compression error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);

    try {
      const { url, error: uploadError } = await uploadDeliveryEvidence(serviceId, type, file);

      if (uploadError) throw uploadError;

      const { error: updateError } = await setServiceEvidenceUrl(serviceId, type, url);

      if (updateError) throw updateError;

      onUploadComplete(url, type);
      handleClose();

    } catch (error) {
      console.error('Error uploading:', error);
      Swal.fire('Error', 'No se pudo subir la foto', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50" 
            onClick={handleClose}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-[50%] z-50 bg-gray-900 border border-gray-800 rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="flex justify-between items-center p-5 border-b border-gray-800">
          <h3 className="font-bold text-[var(--bry-accent)] m-0 text-lg">
            {type === 'pickup' ? 'Evidencia de Recolección' : 'Evidencia de Entrega'}
          </h3>
          <button 
            onClick={handleClose}
            className="w-8 h-8 flex justify-center items-center rounded-full bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        
        <div className="p-5 text-center">
          <p className="text-gray-400 mb-5 text-sm">
            Toma una foto del paquete {type === 'pickup' ? 'al recibirlo' : 'al entregarlo'} para protegerte y dejar constancia.
          </p>

          {preview ? (
            <div className="relative mb-5 inline-block w-full">
              <img 
                src={preview} 
                alt="Preview" 
                className="rounded-2xl max-h-[300px] object-cover w-full shadow-lg border border-gray-800" 
              />
              <button 
                className="absolute top-2 right-2 w-8 h-8 flex justify-center items-center bg-black/60 text-white rounded-full hover:bg-black/80 backdrop-blur-sm"
                onClick={() => { setPreview(null); setFile(null); }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div 
              className="bg-gray-800 rounded-2xl flex flex-col items-center justify-center mb-5 h-[200px] cursor-pointer border-2 border-dashed border-gray-700 hover:border-gray-500 hover:bg-gray-800/80 transition-colors"
              onClick={handleCaptureClick}
            >
              <Camera size={48} className="text-gray-600 mb-3" />
              <span className="text-gray-400 font-bold text-sm">Tocar para tomar foto</span>
            </div>
          )}

          <input 
            type="file" 
            accept="image/*" 
            capture="environment" 
            ref={fileInputRef} 
            className="hidden" 
            onChange={handleFileChange}
          />

          <button 
            className={`w-full rounded-xl py-3.5 font-bold flex items-center justify-center transition-all ${
              !file || loading 
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                : 'bg-[var(--bry-accent)] text-black hover:bg-[#0c959a] shadow-[0_0_15px_rgba(14,171,176,0.3)]'
            }`}
            disabled={!file || loading}
            onClick={handleUpload}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <><Upload size={18} className="mr-2" /> Subir Evidencia</>}
          </button>
        </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default DeliveryEvidence;
